import {CouponErrorCode, getEventId} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {Tag} from '@wix/wix-ui-icons-common'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect, useState} from 'react'
import {ButtonPriority, StatesButtonStates} from 'wix-ui-tpa'
import {StatesButton, Text, TextButton, TextField, ThreeDotsLoader} from 'wix-ui-tpa/cssVars'
import {FormStep} from '../../../constants'
import {usePageActions, usePageStateSelector} from '../../../hooks/state'
import {getEvent} from '../../../selectors/event'
import {couponSubmitted, getInvoiceError, isValidating} from '../../../selectors/invoice'
import {getReservationId} from '../../../selectors/reservation'
import {AccessibilityAlert} from './accessibility-alert'
import {classes} from './coupon-input.st.css'
import {CouponInputProps} from './interfaces'

enum CouponFormState {
  INITIAL = 'INITIAL',
  EDIT = 'EDIT',
  APPLIED = 'APPLIED',
}

export const CouponInput = ({mobile, editStep, currentStep}: CouponInputProps) => {
  const [state, setState] = useState(CouponFormState.INITIAL)
  const [couponCode, setCouponCode] = useState('')
  const {t} = useTranslation()

  const {applyCoupon, removeCoupon} = usePageActions()
  const validating = usePageStateSelector(isValidating)
  const submitted = usePageStateSelector(couponSubmitted)
  const error = usePageStateSelector(getInvoiceError)

  const eventId = getEventId(usePageStateSelector(getEvent))
  const reservationId = usePageStateSelector(getReservationId)

  const paymentStep = currentStep === FormStep.Payment

  const getSubmitButtonState = () => {
    if (validating) {
      return StatesButtonStates.IN_PROGRESS
    } else if (submitted) {
      return StatesButtonStates.SUCCESS
    }
    return StatesButtonStates.IDLE
  }

  const getCouponErrorMessage = (): string | null => {
    if (!error) {
      return null
    }
    switch (error) {
      case CouponErrorCode.ERROR_COUPON_DOES_NOT_EXIST:
        return t('summary.error.couponDoesNotExist')
      case CouponErrorCode.ERROR_COUPON_HAS_EXPIRED:
        return t('summary.error.promotionEnded')
      case CouponErrorCode.ERROR_COUPON_IS_NOT_ACTIVE_YET:
        return t('summary.error.couponNotActiveYet')
      case CouponErrorCode.ERROR_COUPON_USAGE_EXCEEDED:
        return t('summary.error.couponUsageExceeded')
      case CouponErrorCode.CUSTOM_ERROR_TOO_BUSY:
        return t('summary.error.serverBusy')
      default:
        return t('summary.error.unknown')
    }
  }

  const handleSubmit = () => {
    if (validating || submitted) {
      return
    }
    if (paymentStep) {
      editStep(FormStep.BuyerDetails, false)
    }
    applyCoupon(eventId, reservationId, couponCode)
  }

  const handleClear = () => {
    if (validating || submitted) {
      return
    }
    setCouponCode('')
  }

  const handleRemove = () => {
    if (paymentStep) {
      editStep(FormStep.BuyerDetails, false)
    }
    removeCoupon()
    setState(CouponFormState.EDIT)
    setCouponCode('')
  }

  const showEditView = () => setState(CouponFormState.EDIT)

  useEffect(() => {
    if (submitted && !validating) {
      setTimeout(() => {
        setState(CouponFormState.APPLIED)
      }, 1000)
    }
  }, [submitted, validating])

  return (
    <div className={classNames(classes.container, {[classes.mobile]: mobile})}>
      {state === CouponFormState.INITIAL && (
        <TextButton
          className={classNames(classes.textButton, {[classes.desktopFont]: !mobile})}
          onClick={showEditView}
          prefixIcon={<Tag />}
          data-hook={DH.ADD_COUPON_BUTTON}
        >
          {t('couponInput_addCouponCta')}
        </TextButton>
      )}
      {state === CouponFormState.EDIT && (
        <>
          <TextField
            label={t('couponInput_label')}
            placeholder={t('couponInput_placeholder')}
            withClearButton
            error={!!error}
            errorMessage={getCouponErrorMessage()}
            onChange={e => setCouponCode(e.target.value)}
            readOnly={validating || submitted}
            value={couponCode}
            newErrorMessage
            onClear={handleClear}
            className={classes.couponInput}
            aria-label={t('a11y.couponCodeInputField')}
            maxLength={20}
            data-hook={DH.COUPON_CODE_INPUT}
            clearButtonAriaLabel={t('a11y.clearCouponCodeButton')}
            color="red"
          />
          <StatesButton
            state={getSubmitButtonState()}
            idleContent={t('couponInput_applyButton')}
            inProgressContent={
              <>
                <ThreeDotsLoader className={classes.loader} />
                <AccessibilityAlert message={t('a11y.couponCodeApplying')} />
              </>
            }
            onClick={handleSubmit}
            priority={ButtonPriority.basicSecondary}
            className={classes.applyCouponButton}
            data-hook={DH.COUPON_CODE_SUBMIT}
            upgrade
          />
        </>
      )}
      {state === CouponFormState.APPLIED && (
        <div className={classes.applied} data-hook={DH.COUPON_APPLIED}>
          <AccessibilityAlert message={t('a11y.couponCodeApplied')} />
          <div className={classes.coupon}>
            <Tag className={classes.tag} />
            <Text className={classNames(classes.couponCode, {[classes.desktopFont]: !mobile})}>{couponCode}</Text>
          </div>
          <TextButton
            onClick={handleRemove}
            className={classNames(classes.textButton, {[classes.desktopFont]: !mobile})}
            data-hook={DH.COUPON_CODE_CLEAR}
            aria-label={t('a11y.clearCouponCodeButton')}
          >
            {t('couponInput_removeButton')}
          </TextButton>
        </div>
      )}
    </div>
  )
}
